import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { useSearchParams } from 'react-router-dom';

import { enqueueSnackbar } from 'notistack';

import { setAuthForm, setViewerEmail, setViewerPassword } from '@/entities/viewer';

import { AUTH_API } from '@/shared/api/amplify';
import { IS_WHITE_LABEL } from '@/shared/environments';
import { useExecuteOneTime } from '@/shared/hooks/useExecuteOneTime';
import { showErrorAlert } from '@/shared/lib/showErrorAlert';
import { GTMEvents } from '@/shared/thirdParty/gtm';
import { MixpanelEvents } from '@/shared/thirdParty/mixpanel';
import { AuthForm, CognitoErrors } from '@/shared/types/Cognito';
import { AuthError } from '@/shared/types/Error';
import { PasswordInput } from '@/shared/ui/form/PasswordInput';

import { Divider } from '../../components/Divider/styled';
import { EmailInput } from '../../components/EmailInput';
import { FormContainer } from '../../components/FormContainer';
import { GoogleSigninButton } from '../../components/GoogleSigninButton';
import { SubmitButton } from '../../components/SubmitButton/styled';
import { TermsOfService } from '../../components/TermsOfService';
import { DATA_TEST_IDS } from '../../lib/constants';
import { Block, ContentContainer, DividerContainer, Link, Subtitle, TextFieldsContainer } from './styled';

type FormValues = {
  email: string;
  password: string;
};

export const Signup = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const [isLoading, setIsLoading] = useState(false);

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onSubmit',
  });

  const onSubmit = async (input: FormValues) => {
    try {
      setIsLoading(true);

      const { email, password } = input;

      await AUTH_API.signUp(email, password, email);

      setViewerEmail(email);
      setViewerPassword(window.btoa(password));

      setAuthForm(AuthForm.ConfirmEmail);

      reset();
    } catch (e) {
      const error = e as AuthError;

      if (
        error.code === CognitoErrors.UserAlreadyExistsInAuth0 ||
        error.code === CognitoErrors.UserAlreadyExistsInCognito
      ) {
        enqueueSnackbar('An account with the given email already exists.');
      } else {
        showErrorAlert(e);
      }
    }
    setIsLoading(false);
  };

  useExecuteOneTime(() => {
    MixpanelEvents.signUpPageShown();
    GTMEvents.signUpPageShown();
  });

  const switchToSigninForm = () => {
    setAuthForm(AuthForm.Signin);

    if (IS_WHITE_LABEL) {
      searchParams.delete('type');
      setSearchParams(searchParams);
    }
  };

  const subtitle = IS_WHITE_LABEL ? undefined : 'Join 2+ M users to translate any video in minutes with AI';

  return (
    <>
      <FormContainer
        title={`Welcome${IS_WHITE_LABEL ? '' : ' to Rask AI'}`}
        subtitle={subtitle}
        subtitleColor="common.black"
        onSubmit={handleSubmit(onSubmit)}
      >
        <ContentContainer>
          {!IS_WHITE_LABEL && (
            <>
              <GoogleSigninButton />
              <DividerContainer>
                <Divider>
                  <Subtitle>Or Sign Up with e-mail</Subtitle>
                </Divider>
              </DividerContainer>
            </>
          )}
          <TextFieldsContainer>
            <EmailInput control={control} isDisabled={isLoading} />
            <PasswordInput control={control} isDisabled={isLoading} placeholder="Password" />
          </TextFieldsContainer>
          <SubmitButton
            data-testid={DATA_TEST_IDS.signUpButton}
            disabled={isLoading}
            isLoading={isLoading}
            onClick={handleSubmit(onSubmit)}
          >
            Sign up
          </SubmitButton>
          <Block>
            <Subtitle>Do you have an account?</Subtitle>
            <Link data-testid={DATA_TEST_IDS.signInButton} onClick={switchToSigninForm}>
              Sign in
            </Link>
          </Block>
        </ContentContainer>
      </FormContainer>

      {!IS_WHITE_LABEL && <TermsOfService />}
    </>
  );
};
